import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { getAuthRedirectBaseUrl } from "@/lib/authRedirect";
import { sendSystemNotification } from "@/lib/notificationEmail";
import { buildLoginMessage, buildLogoutMessage } from "@/lib/notificationMessages";
import {
  normalizeEmail,
  normalizeUserRole,
  resolveMasterAccess,
} from "@/lib/masterAccess";
import type { User, Session } from "@supabase/supabase-js";
import { canEnableTestLogin } from "@/hooks/testLoginGuard";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isMaster: boolean;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  profile: { full_name: string; phone: string | null } | null;
  professional: { id: string; nickname: string | null; name: string; user_type: string } | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInAsTestUser: () => void;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

type ProfileRow = {
  full_name?: string | null;
  name?: string | null;
  phone?: string | null;
  role?: string | null;
};

type SupabaseErrorLike = {
  code?: string;
  message?: string;
};

type ProfessionalLike = {
  id: string | null;
  user_id?: string | null;
  full_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  email?: string | null;
  role?: string | null;
  user_type?: string | null;
};

type ProfessionalQueryResult = {
  data: ProfessionalLike | null;
  error: SupabaseErrorLike | null;
};

type ProfileWithLegacyColumns = Database["public"]["Tables"]["profiles"]["Row"] & {
  name?: string | null;
};

type AuthSnapshot = {
  isMaster: boolean;
  profile: { full_name: string; phone: string | null } | null;
  professional: { id: string; nickname: string | null; name: string; user_type: string } | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TEST_LOGIN_KEY = "nitfisio-test-login";
const TEST_LOGIN_FEATURE_ENABLED = canEnableTestLogin(
  import.meta.env.DEV === true,
  import.meta.env.VITE_ENABLE_TEST_LOGIN,
);
const TEST_USER = {
  id: "00000000-0000-0000-0000-000000000001",
  email: "qa-test-user@nitfisio.local",
  fullName: "QA Test User",
  nickname: "qa",
};

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMaster, setIsMaster] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [profile, setProfile] = useState<{ full_name: string; phone: string | null } | null>(null);
  const [professional, setProfessional] = useState<{ id: string; nickname: string | null; name: string; user_type: string } | null>(null);
  const onlinePresenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const syncedProfileUserRef = useRef<string | null>(null);
  const syncingProfileRef = useRef(false);
  const authPipelineTokenRef = useRef(0);
  const lastUserSnapshotRef = useRef<Map<string, AuthSnapshot>>(new Map());

  const teardownOnlinePresenceChannel = () => {
    if (!onlinePresenceChannelRef.current) return;
    supabase.removeChannel(onlinePresenceChannelRef.current);
    onlinePresenceChannelRef.current = null;
  };

  const startOnlinePresenceChannel = (userId: string) => {
    teardownOnlinePresenceChannel();

    const channel = supabase.channel("app-online-users", {
      config: { presence: { key: userId } },
    });

    channel.subscribe(async (status) => {
      if (status !== "SUBSCRIBED") return;
      await channel.track({ userId, onlineAt: new Date().toISOString() });
    });

    onlinePresenceChannelRef.current = channel;
  };

  const activateTestUser = () => {
    if (!TEST_LOGIN_FEATURE_ENABLED) {
      localStorage.removeItem(TEST_LOGIN_KEY);
      return;
    }

    startOnlinePresenceChannel(TEST_USER.id);
    setUser({
      id: TEST_USER.id,
      email: TEST_USER.email,
      app_metadata: {},
      user_metadata: { full_name: TEST_USER.fullName },
      aud: "authenticated",
      created_at: new Date().toISOString(),
    } as User);
    setSession(null);
    setIsMaster(false);
    setProfile({ full_name: TEST_USER.fullName, phone: null });
    setProfessional({ id: TEST_USER.id, name: TEST_USER.fullName, nickname: TEST_USER.nickname, user_type: "user" });
    setLoading(false);
    localStorage.setItem(TEST_LOGIN_KEY, "1");
  };

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  const registerLoginTimestamp = async (userId: string) => {
    const lastLoginAt = new Date().toISOString();

    const byUserIdResponse = await supabase
      .from("profiles")
      .update({ last_login: lastLoginAt })
      .eq("user_id", userId)
      .select("id")
      .limit(1);

    const shouldFallback =
      shouldFallbackToProfileId(byUserIdResponse.error) || (byUserIdResponse.data?.length ?? 0) === 0;

    if (!shouldFallback) return;

    const byProfileIdResponse = await supabase
      .from("profiles")
      .update({ last_login: lastLoginAt })
      .eq("id", userId)
      .select("id")
      .limit(1);

    if (byProfileIdResponse.error) {
      throw byProfileIdResponse.error;
    }

    if ((byProfileIdResponse.data?.length ?? 0) === 0) {
      throw new Error("Nenhum perfil encontrado para registrar login");
    }
  };

  const handleLoginNotification = async (authenticatedUser: User) => {
    const executorEmail = authenticatedUser.email?.trim().toLowerCase();
    if (!executorEmail) return;

    // Wait briefly for the Supabase client session to fully propagate
    // before invoking edge functions that require an auth token.
    setTimeout(() => {
      sendSystemNotification({
        notification_type: "login",
        ...buildLoginMessage({
          userName: authenticatedUser.user_metadata?.full_name || authenticatedUser.email || "Usuário",
        }),
        actor_user_id: authenticatedUser.id,
      });
    }, 1500);
  };

  const handleLogoutNotification = async () => {
    await sendSystemNotification({
      notification_type: "logout",
      ...buildLogoutMessage({
        userName: profile?.full_name || user?.user_metadata?.full_name || user?.email || "Usuário",
      }),
      actor_user_id: user?.id,
    });
  };

  const shouldFallbackToProfileId = (error: SupabaseErrorLike | null) => {
    if (!error) return false;
    if (error.code === "42703") return true;

    const normalizedMessage = error.message?.toLowerCase() ?? "";
    return normalizedMessage.includes("user_id") && normalizedMessage.includes("does not exist");
  };

  const isMissingColumnError = (error: SupabaseErrorLike | null, columnName: string) => {
    if (!error) return false;

    const normalizedMessage = error.message?.toLowerCase() ?? "";
    return normalizedMessage.includes(columnName.toLowerCase()) && normalizedMessage.includes("does not exist");
  };

  const fetchProfessionalById = async (table: "v_professionals", userId: string): Promise<ProfessionalQueryResult> => {
    const canonicalResult = await supabase
      .from(table)
      .select("id, full_name, nickname, email, role, user_type")
      .eq("id", userId)
      .maybeSingle();

    if (!isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "full_name") && !isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "role")) {
      return canonicalResult;
    }

    return supabase
      .from(table)
      .select("id, name, nickname, email, user_type")
      .eq("id", userId)
      .maybeSingle();
  };

  const fetchProfessionalByEmail = async (table: "v_professionals", email: string): Promise<ProfessionalQueryResult> => {
    const canonicalResult = await supabase
      .from(table)
      .select("id, full_name, nickname, email, role, user_type")
      .eq("email", email)
      .maybeSingle();

    if (!isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "full_name") && !isMissingColumnError(canonicalResult.error as SupabaseErrorLike | null, "role")) {
      return canonicalResult;
    }

    return supabase
      .from(table)
      .select("id, name, nickname, email, user_type")
      .eq("email", email)
      .maybeSingle();
  };

  const fetchProfileByUser = async (userId: string) => {
    const profileByUserId = await supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle();

    if (!shouldFallbackToProfileId(profileByUserId.error)) return profileByUserId;

    return supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
  };

  const syncAuthenticatedProfile = async (authUser?: User | null) => {
    try {
      const user = authUser ?? (await supabase.auth.getUser()).data.user;
      if (!user) return;

      if (syncingProfileRef.current) return;
      if (syncedProfileUserRef.current === user.id) return;

      if (!UUID_V4ISH_REGEX.test(user.id)) {
        console.error("[auth] Invalid UUID for profile sync", { userId: user.id });
        return;
      }

      syncingProfileRef.current = true;

      const existingProfessionalByUserRes = await fetchProfessionalById("v_professionals", user.id);
      const normalizedUserEmail = normalizeEmail(user.email);

      const [existingProfileByUserRes, existingProfileByIdRes, existingProfessionalByEmailRes] = await Promise.all([
        supabase.from("profiles").select("role, full_name, name, email").eq("user_id", user.id).maybeSingle(),
        supabase.from("profiles").select("role").eq("id", user.id).maybeSingle(),
        user.email
          ? fetchProfessionalByEmail("v_professionals", normalizedUserEmail)
          : Promise.resolve({ data: null, error: null }),
      ]);

      const hasMasterAccess = resolveMasterAccess({
        profileRole: existingProfileByUserRes.data?.role,
        fallbackProfileRole: existingProfileByIdRes.data?.role,
        professionalType: existingProfessionalByUserRes.data?.user_type,
        professionalRole: existingProfessionalByEmailRes.data?.role,
        email: user.email,
      });
      const currentProfileRole = existingProfileByUserRes.data?.role ?? existingProfileByIdRes.data?.role;
      const effectiveRole = hasMasterAccess
        ? "master"
        : (currentProfileRole && currentProfileRole !== "user" ? currentProfileRole : "professional");

      const existingFullName = existingProfileByUserRes.data?.full_name || existingProfileByUserRes.data?.name;
      const metadataName = user.user_metadata?.name ?? user.user_metadata?.full_name ?? "";
      const existingProfileRole = existingProfileByUserRes.data?.role;
      const existingProfileEmail = normalizeEmail((existingProfileByUserRes.data as { email?: string | null } | null)?.email);
      const normalizedUserEmailForProfile = normalizeEmail(user.email);

      const shouldSkipUpsert = Boolean(
        existingProfileByUserRes.data
        && existingProfileRole === effectiveRole
        && existingProfileEmail === normalizedUserEmailForProfile
        && existingFullName,
      );

      if (shouldSkipUpsert) {
        syncedProfileUserRef.current = user.id;
        return;
      }

      const payload: any = {
        user_id: user.id,
        email: user.email ?? "",
        role: effectiveRole,
        updated_at: new Date().toISOString(),
      };

      // Only set name if profile doesn't already have a full_name
      if (!existingFullName) {
        payload.name = metadataName;
      }

      

      const response = await supabase.from("profiles").upsert(payload, { onConflict: "user_id" });

      if (response.error) {
        console.error("[auth] profile upsert failed", {
          userId: user.id,
          error: response.error,
        });
        return;
      }

      syncedProfileUserRef.current = user.id;
      
    } catch (error) {
      console.error("[auth] unexpected error while syncing profile", error);
    } finally {
      syncingProfileRef.current = false;
    }
  };

  const fetchUserData = async (userId: string, userEmail?: string | null): Promise<AuthSnapshot> => {
    const normalizedEmail = normalizeEmail(userEmail) || null;

    const [profileRes, professionalByUserIdRes, professionalByEmailRes] = await Promise.all([
      fetchProfileByUser(userId),
      fetchProfessionalById("v_professionals", userId),
      normalizedEmail
        ? fetchProfessionalByEmail("v_professionals", normalizedEmail)
        : Promise.resolve({ data: null, error: null }),
    ]);

    const professionalData: ProfessionalLike | null =
      professionalByUserIdRes.data
      ?? professionalByEmailRes.data
      ?? null;

    const normalizedProfessionalType = normalizeUserRole(
      professionalData?.user_type ?? professionalData?.role,
    );
    const profileRow = profileRes.data as ProfileWithLegacyColumns | null;

    return {
      isMaster: resolveMasterAccess({
        professionalType: professionalData?.user_type,
        professionalRole: professionalData?.role,
        profileRole: (profileRow as ProfileRow | null)?.role,
        metadataRole: (user?.user_metadata as { role?: string } | undefined)?.role,
        email: userEmail,
      }),
      profile: profileRow
        ? {
            full_name: profileRow.full_name ?? profileRow.name ?? "",
            phone: profileRow.phone ?? null,
          }
        : null,
      professional:
        professionalData?.id
          ? {
              id: professionalData.id,
              nickname: professionalData.nickname ?? null,
              name:
                professionalData.full_name?.trim()
                || professionalData.name?.trim()
                || profileRow?.full_name?.trim()
                || normalizedEmail?.split("@")[0]
                || "Profissional",
              user_type: normalizedProfessionalType,
            }
          : null,
    };
  };

  const applyAuthSnapshot = (snapshot: AuthSnapshot) => {
    setIsMaster(snapshot.isMaster);
    setProfile(snapshot.profile);
    setProfessional(snapshot.professional);
  };

  const processAuthSession = async (event: string, nextSession: Session | null) => {
    const pipelineToken = ++authPipelineTokenRef.current;

    if (!nextSession?.user) {
      syncedProfileUserRef.current = null;
      teardownOnlinePresenceChannel();
      setSession(null);
      setUser(null);
      setIsMaster(false);
      setProfile(null);
      setProfessional(null);
      setLoading(false);
      return;
    }

    const nextUser = nextSession.user;
    setSession(nextSession);
    setUser(nextUser);

    const cachedSnapshot = lastUserSnapshotRef.current.get(nextUser.id);
    if (cachedSnapshot) {
      applyAuthSnapshot(cachedSnapshot);
      setLoading(false);
    } else {
      setLoading(true);
    }

    startOnlinePresenceChannel(nextUser.id);

    if (event === "SIGNED_IN") {
      await syncAuthenticatedProfile(nextUser);
    }

    try {
      const snapshot = await fetchUserData(nextUser.id, nextUser.email);
      if (pipelineToken !== authPipelineTokenRef.current) return;

      lastUserSnapshotRef.current.set(nextUser.id, snapshot);
      applyAuthSnapshot(snapshot);
    } catch (error) {
      console.error("[auth] failed to fetch user data snapshot", {
        userId: nextUser.id,
        event,
        error,
      });
      if (pipelineToken !== authPipelineTokenRef.current) return;
      applyAuthSnapshot({
        isMaster: false,
        profile: null,
        professional: null,
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (TEST_LOGIN_FEATURE_ENABLED && localStorage.getItem(TEST_LOGIN_KEY) === "1") {
      activateTestUser();
      return;
    }

    if (!TEST_LOGIN_FEATURE_ENABLED) {
      localStorage.removeItem(TEST_LOGIN_KEY);
    }

    let subscriptionRef: { unsubscribe: () => void } | null = null;

    const initializeAuth = async () => {
      try {
        // 1. Restore session from storage FIRST to avoid stale refresh-token errors
        const { data: { session: restoredSession }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.warn("[auth] stale session detected, signing out", sessionError.message);
          await supabase.auth.signOut();
          await processAuthSession("INITIAL_SESSION", null);
        } else {
          await processAuthSession("INITIAL_SESSION", restoredSession);
        }
      } catch (error) {
        console.error("[auth] failed to initialize auth session", error);
        setSession(null);
        setUser(null);
        setIsMaster(false);
        setProfile(null);
        setProfessional(null);
      } finally {
        setLoading(false);
      }

      // 2. AFTER session is restored, subscribe to subsequent auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        // Skip the INITIAL_SESSION event — we already handled it above
        if (event === "INITIAL_SESSION") return;

        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
        }
        void processAuthSession(event, session);
      });
      subscriptionRef = subscription;
    };

    initializeAuth();

    return () => {
      teardownOnlinePresenceChannel();
      subscriptionRef?.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    const credentialsToTry = [password];
    if (trimmedPassword && trimmedPassword !== password) {
      credentialsToTry.push(trimmedPassword);
    }

    let error: Error | null = null;
    let data: { user: User | null } | null = null;

    for (const candidatePassword of credentialsToTry) {
      const signInResult = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: candidatePassword,
      });

      error = signInResult.error as Error | null;
      data = signInResult.data;

      if (!error) {
        break;
      }
    }

    // Record login timestamp and notification
    if (!error && data.user) {
      // Check if user is blocked
      const { data: profileData } = await supabase
        .from("profiles")
        .select("blocked")
        .or(`user_id.eq.${data.user.id},id.eq.${data.user.id}`)
        .limit(1)
        .maybeSingle();

      if (profileData?.blocked) {
        await supabase.auth.signOut();
        return { error: new Error("Seu acesso foi bloqueado. Entre em contato com o administrador.") };
      }

      // Ensure profile exists BEFORE trying to register login timestamp
      await syncAuthenticatedProfile(data.user);
      registerLoginTimestamp(data.user.id).catch((loginTimestampError) => {
        console.error("Failed to register login timestamp:", loginTimestampError);
      });
      handleLoginNotification(data.user).catch((loginNotificationError) => {
        console.error("Failed to register login notification:", loginNotificationError);
      });
    }
    return { error };
  };

  const signInAsTestUser = () => {
    if (!TEST_LOGIN_FEATURE_ENABLED) return;

    activateTestUser();
    registerLoginTimestamp(TEST_USER.id).catch((loginTimestampError) => {
      console.error("Failed to register test login timestamp:", loginTimestampError);
    });
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const { error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: getAuthRedirectBaseUrl(),
        data: {
          full_name: fullName,
          role: "user",
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Send notification BEFORE signing out so the session is still valid
    try {
      await handleLogoutNotification();
    } catch (logoutNotificationError) {
      console.error("Failed to register logout notification:", logoutNotificationError);
    }
    localStorage.removeItem(TEST_LOGIN_KEY);
    teardownOnlinePresenceChannel();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsMaster(false);
    setProfile(null);
    setProfessional(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isMaster, isPasswordRecovery, clearPasswordRecovery, profile, professional, signIn, signInAsTestUser, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
